<template>
  <Tab
    :value="value"
    :disabled="disabled"
    :as="as"
    :as-child="asChild"
    :unstyled="true"
    :pt="passThrough"
    class="meridia-tab"
    v-bind="$attrs"
  >
    <div
      v-if="hasIcon"
      class="meridia-tab-content inline-flex items-center justify-center w-full gap-0"
      v-bind="getPt('tabContent', passThrough)"
    >
      <slot name="icon" v-if="$slots.icon" />
      <MeridiaIcon
        v-else-if="props.icon"
        :name="props.icon"
        :type="props.iconType"
        class="meridia-tab-icon"
        v-bind="getPt('icon', passThrough)"
      />
      <span class="meridia-tab-label" v-bind="getPt('label', passThrough)"
        ><slot
      /></span>
    </div>
    <slot v-else />
  </Tab>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue';
import Tab from 'primevue/tab';
import { usePassThrough } from 'primevue/passthrough';
import type { PassThrough } from '@primevue/core';
import type {
  MeridiaTabProps,
  MeridiaTabPassThroughOptions,
} from 'src/design_system/components/tab/types';
import MeridiaIcon from 'src/design_system/components/icon/MeridiaIcon.vue';
import { getPt } from 'src/design_system/components/utils';

const props = withDefaults(defineProps<MeridiaTabProps>(), {
  disabled: false,
  asChild: false,
  iconType: 'material-symbols-outlined',
});

// Note: tab-order tracking (for directional transitions) is owned by MeridiaTabPanel,
// which registers each panel's value with MeridiaTabs. Panels are always present, so they
// are the single source of order — headers (this component) do not need to register.

defineSlots<{
  default: () => any;
  icon: () => any;
}>();

const slots = useSlots();
const hasIcon = computed(() => !!props.icon || !!slots.icon);

const passThrough = computed(
  () =>
    usePassThrough(
      props.pt,
      {
        root: (options) => ({
          class: [
            'meridia-tab-root inline-flex items-center justify-center gap-2 select-none whitespace-nowrap transition-colors duration-200 outline-none flex-1 min-w-0 h-full px-0 w-full text-medium uppercase font-bold mr-[1px] last:mr-0',
            options?.props?.disabled
              ? 'meridia-tab-root--disabled text-neutral-text-disabled bg-neutral-surface-disabled !cursor-not-allowed'
              : [
                  'cursor-pointer text-neutral-text-body focus-visible:ring-2 focus-visible:ring-primary-border-default',
                  options?.context?.active
                    ? 'meridia-tab-root--active bg-neutral-surface-default hover:bg-neutral-surface-default'
                    : 'meridia-tab-root--inactive bg-neutral-surface-muted hover:bg-neutral-surface-disabled',
                ],
          ],
        }),
        icon: {
          class: 'mr-xs icon-s shrink-0 leading-none text-inherit',
        },
      } as MeridiaTabPassThroughOptions,
      { mergeSections: true, mergeProps: true }
    ) as PassThrough<MeridiaTabPassThroughOptions>
);
</script>
